'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useNowMs } from '@/hooks/useNowMs';
import { useDisplayTimeZone } from '@/contexts/DisplayTimeZoneContext';
import { todayKeyInZone } from '@/app/[locale]/event/[eventId]/libs/matchesDay';
import { useEventLiveScores } from '@/app/[locale]/event/[eventId]/[slug]/components/useEventLiveScores';
import {
  applyDrawScores,
  isEventActiveOnDay,
} from '@/app/[locale]/event/[eventId]/libs/liveScoreViews';
import type { DrawResult, EventMatchSchedule } from '@/draw/types';
import { applyLuckyLoserLabel, getLuckyLoserPlayerIds } from '@/draw/lucky-loser';
import { activeSeedingRegistrants } from '@/draw/seedings';
import { eventPlayerHref } from '@/app/[locale]/event/[eventId]/libs/eventPlayers';
import { PublicDrawView } from './PublicDrawView';
import { drawMatchFacts } from '../libs/drawMatchFacts';
import { parsePlayedGameScores } from '@/draw/core/standingsCalculator';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ExclamationTriangleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { publicGet } from '@/lib/api';
import type { Database } from '@/types/supabase';
import type {
  PublicDrawEvent,
  PublicDrawPayload,
} from '@/app/[locale]/event/[eventId]/libs/publicDrawView';
import { DrawViewHeader } from './DrawViewHeader';
import { DrawSeedings } from './DrawSeedings';
import { isDrawPubliclyVisible } from '@/lib/appDrawProjection';
import { EventTimeZoneNote } from '@/app/[locale]/event/[eventId]/[slug]/components/EventTimeZoneNote';
import { createByePlayer } from '@/draw/utils/seeding';
import { getTemplate } from '@/draw/templates/ladderTemplates';
import { NameFormattingProvider } from '@/contexts/NameFormattingContext';
import {
  buildTeamLabelMap,
  buildTeamEntryMap,
  type TeamEntry,
  applyTeamLabelsToStandings,
} from '@/app/[locale]/event/[eventId]/libs/teamLabels';

type Draw = Database['public']['Tables']['draw']['Row'];
type EventGroup = Database['public']['Tables']['event_group']['Row'];

type DrawWithGroup = Draw & {
  event_group: Pick<
    EventGroup,
    'id' | 'name' | 'group_type' | 'max_participants' | 'sort_order' | 'is_team'
  >;
};

type ViewPlayer = {
  id: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  alias?: string | null;
  isBye: boolean;
};

function mapDbPlayerToView(
  p:
    | { id: string; first_name: string; last_name: string; alias?: string | null }
    | null
    | undefined,
  opts: {
    playerPosition?: 'player1' | 'player2';
    isPlayerBye?: boolean;
    luckyLoserPlayerIds?: Set<string>;
    luckyLoserLabel: string;
    teamLabels: Map<string, string>;
  },
): ViewPlayer | undefined {
  const { playerPosition = 'player1', isPlayerBye = false, luckyLoserPlayerIds, teamLabels } = opts;

  // If player exists, map normally
  if (p) {
    const isBye = isPlayerBye || p.first_name === 'BYE' || p.id.startsWith('bye-');
    if (isBye) {
      const bye = createByePlayer(p.id);
      return { id: bye.id, firstName: bye.firstName, lastName: bye.lastName, isBye: true };
    }
    // Substitute "Lucky Loser" placeholder if this slot is LL-pending.
    const labelled = applyLuckyLoserLabel(p, luckyLoserPlayerIds, opts.luckyLoserLabel);
    // A pending Lucky Loser slot keeps its placeholder; a real entrant in a team
    // division carries the team name it registered with.
    const isLLPlaceholder = luckyLoserPlayerIds?.has(p.id) ?? false;
    const displayName = isLLPlaceholder ? undefined : teamLabels.get(p.id);
    return {
      id: labelled!.id,
      firstName: labelled!.first_name,
      lastName: labelled!.last_name,
      ...(displayName ? { displayName } : {}),
      ...(isLLPlaceholder ? {} : { alias: p.alias ?? null }),
      isBye: false,
    };
  }

  // If player is null but this specific player is a bye, create a BYE player
  if (isPlayerBye) {
    const byeId = `bye-${playerPosition}`;
    return { id: byeId, firstName: 'BYE', lastName: '', isBye: true };
  }

  // Otherwise return undefined for null players
  return undefined;
}

interface DrawViewClientProps {
  event: PublicDrawEvent;
  draw: DrawWithGroup;
  locale: string;
  slug: string;
  ratingsEnabled?: boolean;
  /** The draw read on the server; `null` when that read failed (retried via the route). */
  initialPayload: PublicDrawPayload | null;
}

const FETCH_FAILED = 'Failed to fetch draw data';
// Flush-driven asks for one lagging topology (~1 min of 10s flushes). Past this the
// stored revision is stuck (flush stalled, read failing), and asking on every score
// flush would only cost requests; the next topology change starts over.
const LAG_RETRY_LIMIT = 6;

export function DrawViewClient({
  event: _event,
  draw,
  locale: _locale,
  slug,
  ratingsEnabled = true,
  initialPayload,
}: DrawViewClientProps) {
  const t = useTranslations('Event.drawView');
  const drawVisible = isDrawPubliclyVisible(draw);
  const startsFailed = drawVisible && initialPayload === null;
  const [payload, setPayload] = useState(initialPayload);
  const [error, setError] = useState<string | null>(startsFailed ? FETCH_FAILED : null);
  const [currentTab, setCurrentTab] = useState<'draws' | 'seedings'>('draws');
  const nowMs = useNowMs(30_000);
  const zone = useDisplayTimeZone() ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = nowMs === null ? null : todayKeyInZone(nowMs, zone);
  const live = useEventLiveScores(
    _event.id,
    drawVisible && isEventActiveOnDay(_event, today, zone),
  );
  // The newest live topology the shown draw already reflects.
  const satisfiedTopology = useRef(initialPayload?.topologyRevision ?? 0);
  const requestTopology = useRef<((version: number) => void) | null>(null);
  // The live feed's publication revision: it bumps on every flush.
  const liveRevision = useRef(live.revision);
  const onFlush = useRef<(() => void) | null>(null);

  const transformToDrawResult = useCallback(
    (complete: PublicDrawPayload): DrawResult => {
      const luckyLoserPlayerIds = getLuckyLoserPlayerIds(complete.seeding);
      const luckyLoserLabel = t('luckyLoser');
      // Team divisions: label participants by the team name they registered with.
      const teamLabels = buildTeamLabelMap(draw.event_group, complete.registrants);
      const toView = (
        p:
          | { id: string; first_name: string; last_name: string; alias?: string | null }
          | null
          | undefined,
        playerPosition: 'player1' | 'player2' = 'player1',
        isPlayerBye = false,
      ) =>
        mapDbPlayerToView(p, {
          playerPosition,
          isPlayerBye,
          luckyLoserPlayerIds,
          luckyLoserLabel,
          teamLabels,
        });
      const standings = complete.standings.map((stage) => ({
        ...stage,
        lastUpdated: new Date(stage.lastUpdated),
        standings:
          teamLabels.size === 0
            ? stage.standings
            : applyTeamLabelsToStandings(stage.standings, (id) => teamLabels.get(id)),
      }));
      return {
        drawId: complete.draw.id,
        name: complete.draw.name,
        type: complete.draw.format_type as 'elimination' | 'round_robin',
        drawSize: complete.draw.draw_size,
        options: (complete.draw.settings as Record<string, unknown>) || {},
        stages: complete.stages.map((stage) => {
          const stageSettings = stage.settings as Record<string, unknown> | null;
          const templateId = stageSettings?.ladderTemplateId as string | undefined;
          const ladderTemplate = templateId ? getTemplate(templateId) : undefined;
          return {
            stageId: stage.stage_id,
            name: stage.name,
            type: stage.type as 'elimination' | 'round_robin',
            order: stage.order,
            context: stage.context as
              | 'main'
              | 'plate'
              | 'playoff'
              | 'consolation'
              | 'qualifying'
              | undefined,
            settings: stageSettings ?? undefined,
            ...(ladderTemplate ? { ladderTemplate } : {}),
            rounds: complete.rounds
              .filter((round) => round.stage_id === stage.id)
              .map((round) => ({
                roundId: round.round_id || `round-${round.round_number}`,
                name: round.name,
                roundNumber: round.round_number,
                matches: complete.matches
                  .filter((match) => match.round_id === round.id)
                  .map((match) => ({
                    matchId: match.draw_match_id ?? `match-${match.id}`,
                    position: match.position || match.match_number,
                    player1: toView(match.player_1, 'player1', match.player1_is_bye || false),
                    player2: toView(match.player_2, 'player2', match.player2_is_bye || false),
                    isBye: match.is_bye || false,
                    isWalkover: match.is_walkover || false,
                    status: match.status as 'pending' | 'in_progress' | 'completed',
                    outcome: match.outcome ?? null,
                    winner: toView(match.winner),
                    loser: toView(match.loser),
                    // The games played, a retirement's unfinished game included.
                    score: parsePlayedGameScores(match).map(({ completed: _c, ...g }) => g),
                    ...drawMatchFacts(match),
                    eventMatchSchedule: match.event_match_schedule
                      ? (match.event_match_schedule as EventMatchSchedule)
                      : null,
                  })),
              })),
          };
        }),
        matchProgression: complete.progressions,
        standings,
      };
    },
    [draw.event_group, t],
  );

  const transformToSeedings = useCallback(
    (
      complete: PublicDrawPayload,
    ): Array<{
      seedNumber: number | null;
      drawPosition: number | null;
      pointsAtSeeding: number | null;
      pointsType: string | null;
      manualOverride: boolean | null;
      player: {
        id: string;
        firstName: string;
        lastName: string;
        username: string | null;
        alias?: string | null;
        currentRating: number | null;
        profilePhoto: string | null;
      };
      team: TeamEntry | null;
    }> => {
      // Index seeding by player_id for quick lookup
      const seedingByPlayerId = new Map(
        (complete.seeding || []).map((s) => [s.player_id || '', s]),
      );
      // Team divisions: the team each registrant registered with (name + roster).
      const teamEntries = buildTeamEntryMap(draw.event_group, complete.registrants);

      // Build list from the active registrants (withdrawn leave Seedings; the team
      // map above keeps the full list), merge seeding details if present
      const merged = activeSeedingRegistrants(complete.registrants).map((r) => {
        const s = seedingByPlayerId.get(r.player.id);
        return {
          seedNumber: s?.seed_number ?? null,
          drawPosition: s?.draw_position ?? null,
          pointsAtSeeding:
            (s?.points_at_seeding as number | null) ??
            (r.player.current_rating as number | null) ??
            null,
          pointsType: (s?.points_type as string | null) ?? 'rating',
          manualOverride: (s?.manual_override as boolean | null) ?? null,
          player: {
            id: r.player.id,
            firstName: r.player.first_name,
            lastName: r.player.last_name,
            username: r.player.username,
            alias: r.player.alias,
            currentRating: r.player.current_rating,
            profilePhoto: r.player.profile_photo,
          },
          team: teamEntries.get(r.player.id) ?? null,
        };
      });

      return merged;
    },
    [draw.event_group],
  );

  const drawData = useMemo(
    () => (payload ? transformToDrawResult(payload) : null),
    [payload, transformToDrawResult],
  );
  const seedings = useMemo(
    () => (payload ? transformToSeedings(payload) : []),
    [payload, transformToSeedings],
  );
  const liveDraw = useMemo(
    () => (drawData ? applyDrawScores(drawData, draw.id, live.byScheduleId) : null),
    [drawData, draw.id, live.byScheduleId],
  );

  // Structure comes from the server; refetch it (cached route) only when the live
  // topology moves past what the shown draw reflects. Requests coalesce to the
  // newest revision; a failure keeps the last good draw and retries every 60s
  // while visible. The live snapshot reports the topology before the ~10s flush
  // stores it, and the route answers with the stored revision, so a response can
  // lag the request: it is applied and only its own revision is satisfied, and
  // the request is repeated once a flush (which is what stores it) has landed.
  useEffect(() => {
    if (!drawVisible) return;
    let cancelled = false;
    let inFlight = false;
    // undefined: nothing to fetch; null: the base payload; n: live topology n.
    let wanted: number | null | undefined = startsFailed ? null : undefined;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // Set while `version` has had a lagging answer: the live revision it was asked
    // at, and how many flush-driven asks it has had.
    let lag: { version: number; askedAt: number; retries: number } | null = null;
    const waitingForFlush = () =>
      lag !== null && lag.version === wanted && liveRevision.current <= lag.askedAt;
    const stopRetry = () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      retryTimer = null;
    };
    const scheduleRetry = (delayMs: number) => {
      if (document.hidden) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void run();
      }, delayMs);
    };
    const run = async () => {
      if (typeof wanted === 'number' && wanted <= satisfiedTopology.current) wanted = undefined;
      if (cancelled || inFlight || wanted === undefined || document.hidden) return;
      if (waitingForFlush()) return;
      stopRetry();
      const version = wanted;
      const askedAt = liveRevision.current;
      inFlight = true;
      let failed = false;
      try {
        const next = await publicGet<PublicDrawPayload>(
          `/event/${_event.id}/draw/${draw.id}${version === null ? '' : `?live_topology=${version}`}`,
        );
        if (cancelled) return;
        satisfiedTopology.current = Math.max(satisfiedTopology.current, next.topologyRevision);
        if (version !== null && next.topologyRevision < version) {
          const retries = lag?.version === version ? lag.retries + 1 : 0;
          lag = retries < LAG_RETRY_LIMIT ? { version, askedAt, retries } : null;
          if (lag === null && wanted === version) wanted = undefined;
        } else {
          lag = null;
          if (wanted === version) wanted = undefined;
        }
        setPayload(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        failed = true;
        console.error('Error fetching draw data:', err);
        setError(err instanceof Error ? err.message : FETCH_FAILED);
      } finally {
        inFlight = false;
        if (!cancelled && wanted !== undefined) {
          if (failed && wanted === version) scheduleRetry(60_000);
          else void run();
        }
      }
    };
    requestTopology.current = (version) => {
      if (version <= satisfiedTopology.current) return;
      wanted = typeof wanted === 'number' ? Math.max(wanted, version) : version;
      void run();
    };
    // Only a lagging wait reacts to flushes; a failure keeps its 60s backoff.
    onFlush.current = () => {
      if (retryTimer === null && lag !== null && lag.version === wanted) void run();
    };
    const onVisibility = () => {
      if (document.hidden) stopRetry();
      else void run();
    };
    if (startsFailed) scheduleRetry(60_000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stopRetry();
      requestTopology.current = null;
      onFlush.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [_event.id, draw.id, drawVisible, startsFailed]);

  // Before the topology effect: a snapshot that moves both asks at its own revision.
  useEffect(() => {
    liveRevision.current = live.revision;
    onFlush.current?.();
  }, [live.revision]);

  useEffect(() => {
    requestTopology.current?.(live.topologyRevision);
  }, [live.topologyRevision, _event.id, draw.id, drawVisible, startsFailed]);

  function renderDrawsContent() {
    if (drawVisible && !payload && error) {
      return (
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-lg font-semibold">{t('errorTitle')}</h2>
            <p className="text-muted text-sm">{t('errorDescription')}</p>
          </div>
          <Alert className="border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertDescription className="text-red-800 dark:text-red-200">{error}</AlertDescription>
          </Alert>
        </div>
      );
    }

    // Same rule as the Divisions tab's draw door and the app projection:
    // published / active / completed render; anything else is "not available".
    if (!drawVisible) {
      return (
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-lg font-semibold">{t('drawNotAvailable')}</h2>
            <p className="text-muted text-sm">{t('drawNotAvailableDescription')}</p>
          </div>
          <div>
            <div className="flex flex-col items-center justify-center py-12">
              <DocumentTextIcon className="text-muted mb-4 h-12 w-12" />
              <p className="text-muted text-center">
                {draw.status === 'draft'
                  ? t('drawNotGenerated')
                  : draw.status === 'active'
                    ? t('drawInProgress')
                    : t('drawNotAvailable')}
              </p>
            </div>
          </div>
        </div>
      );
    }
    if (!drawData) {
      return (
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-lg font-semibold">{t('noDrawData')}</h2>
            <p className="text-muted text-sm">{t('noDrawDataDescription')}</p>
          </div>
          <div>
            <div className="flex flex-col items-center justify-center py-12">
              <DocumentTextIcon className="text-muted mb-4 h-12 w-12" />
              <p className="text-muted text-center">{t('noDrawDataMessage')}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <PublicDrawView
        draw={liveDraw ?? drawData}
        playerHref={(id) =>
          `/${_locale}${eventPlayerHref(_event.id, slug, id, draw.event_group.id)}`
        }
      />
    );
  }

  function renderSeedingsContent() {
    return (
      <DrawSeedings
        playerHref={(id) =>
          `/${_locale}${eventPlayerHref(_event.id, slug, id, draw.event_group.id)}`
        }
        embedded
        seedings={seedings}
        showRatings={ratingsEnabled}
        isTeamDivision={draw.event_group.is_team === true}
      />
    );
  }

  return (
    <NameFormattingProvider isJapanese={payload?.isJapanese ?? false}>
      <Card className="w-full min-w-0 p-3 md:p-6 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
        <div className="mb-2 min-w-0 md:mb-4">
          <DrawViewHeader
            title={draw.event_group.name}
            backHref={`/${_locale}/event/${_event.id}/${slug}/divisions`}
            currentTab={currentTab}
            onTabChange={setCurrentTab}
            note={<EventTimeZoneNote at={_event.start_date} />}
          />
        </div>

        {/* Tab Content */}
        {currentTab === 'draws' ? renderDrawsContent() : renderSeedingsContent()}
      </Card>
    </NameFormattingProvider>
  );
}
